
import React, { useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Mail, MessageCircle, ClipboardCheck, PhoneCall, ShieldCheck, Anchor, Luggage, Clock, MapPin, Building2, LandPlot, Info, Sparkles, Handshake, Calendar, X, ExternalLink } from 'lucide-react';
import { ASSETS } from '../constants';

export const Booking: React.FC = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  // 🔴 链接设置中心：
  // 已根据您的要求更新为腾讯问卷链接。
  // 腾讯问卷在微信生态内打开体验极佳，且支持无缝登录。
  const BOOKING_URL = "https://wj.qq.com/s2/25674950/c5bb/"; 

  return (
    <div className="flex flex-col">
      <SectionWrapper>
        {/* 头部：标题 */}
        <div className="text-center mb-20 md:mb-28 animate-fade-in flex flex-col items-center">
          <h2 className="font-serif text-3xl md:text-5xl text-stone-900 tracking-widest mb-4 font-black">
            预约设置
          </h2>
          <div className="flex flex-col items-center gap-2">
            <span className="text-stone-400 font-sans uppercase text-xs tracking-[0.6em] font-black">The Voyage Contract</span>
            <p className="text-stone-700 font-serif text-xl mt-10 max-w-xl leading-loose italic">
              “稳定的设置，是容纳内在惊涛骇浪的容器。”
            </p>
          </div>
        </div>

        {/* 第一部分：航行地图 */}
        <div className="max-w-6xl mx-auto mb-16 relative px-4">
          <div className="absolute top-[60px] left-[10%] right-[10%] h-48 hidden md:block -z-10 opacity-30">
            <svg width="100%" height="100%" viewBox="0 0 1000 150" fill="none" preserveAspectRatio="none">
              <path 
                d="M0,75 C150,180 350,-40 500,75 C650,180 850,-40 1000,75" 
                stroke="#859460" 
                strokeWidth="2.5" 
                strokeDasharray="12,12" 
                className="animate-[dash_45s_linear_infinite]"
              />
              <path d="M990,65 L1000,75 L990,85" stroke="#859460" strokeWidth="2.5" fill="none" />
            </svg>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-16 md:gap-4 items-start relative z-10">
             {/* 起点 */}
             <div className="flex flex-col items-center text-center group">
                <div className="relative mb-8">
                   <div className="w-28 h-28 bg-white rounded-full flex items-center justify-center shadow-2xl border-4 border-sage-50 group-hover:scale-105 transition-all duration-700">
                      <div className="w-20 h-20 bg-sage-600 rounded-full flex items-center justify-center text-white shadow-lg">
                         <Anchor size={32} />
                      </div>
                   </div>
                   <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-white px-5 py-2.5 border border-stone-200 rounded-sm shadow-xl whitespace-nowrap">
                      <p className="text-xs font-serif text-stone-800 flex items-center gap-2 font-black">
                         <Clock size={14} className="text-amber-500" /> 
                         频率时长：<span className="text-stone-900 underline decoration-amber-300 underline-offset-4">50min / 周 1-2次</span>
                      </p>
                   </div>
                </div>
                <div className="space-y-3 px-2">
                   <h4 className="font-serif text-2xl font-black text-stone-900">开始：锚点</h4>
                   <p className="text-stone-500 font-mono text-xs tracking-[0.3em] uppercase mb-1 font-black">0-4 次初始评估</p>
                   <p className="text-stone-800 text-sm leading-relaxed font-serif font-bold italic">确定航行坐标，<br/>双方是否匹配。</p>
                </div>
             </div>

             {/* 过程群岛 */}
             <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-0 relative py-10">
                {[
                  { label: '短程咨询', count: '约 25 次', color: 'bg-amber-400' },
                  { label: '中程咨询', count: '约 40 次', color: 'bg-rose-400' },
                  { label: '长程咨询', count: '100 次 +', color: 'bg-stone-700' }
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center group">
                     <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 shadow-md border border-stone-100 group-hover:scale-110 transition-all duration-500">
                        <div className={`w-10 h-10 ${item.color} rounded-full flex items-center justify-center text-white shadow-inner`}>
                           <LandPlot size={18} />
                        </div>
                     </div>
                     <h5 className="font-serif text-base font-black text-stone-900 mb-1">{item.label}</h5>
                     <p className="text-stone-600 font-mono text-[10px] tracking-widest uppercase font-black">{item.count}</p>
                  </div>
                ))}
             </div>

             {/* 终点：行囊 */}
             <div className="flex flex-col items-center text-center group">
                <div className="relative mb-8">
                   <div className="w-28 h-28 bg-rose-50 rounded-full flex items-center justify-center shadow-2xl border-4 border-rose-100 group-hover:scale-105 transition-all duration-700">
                      <div className="w-20 h-20 bg-rose-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-rose-200/50">
                         <Luggage size={32} />
                      </div>
                   </div>
                   <div className="absolute inset-0 bg-rose-300 rounded-full blur-3xl opacity-20 -z-10 animate-pulse"></div>
                   <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-white px-5 py-2.5 border border-stone-200 rounded-sm shadow-xl whitespace-nowrap">
                      <p className="text-xs font-serif text-stone-800 flex items-center gap-2 font-black">
                         <MapPin size={14} className="text-rose-400" /> 
                         真实着陆：<span className="text-stone-900 underline decoration-rose-300 underline-offset-4">有条件也可地面见面</span>
                      </p>
                   </div>
                </div>
                <div className="space-y-3 px-2">
                   <h4 className="font-serif text-2xl font-black text-stone-900">结束：行囊</h4>
                   <p className="text-stone-500 font-mono text-xs tracking-[0.3em] uppercase mb-1 font-black">抵达彼岸</p>
                   <p className="text-stone-800 text-sm leading-relaxed font-serif font-bold italic">带上充足行囊，<br/>开启新的旅程。</p>
                </div>
             </div>
          </div>
        </div>

        {/* 核心引用语 */}
        <div className="max-w-4xl mx-auto text-center mt-4 mb-24 md:mb-32 px-6">
           <p className="text-stone-900 text-xl md:text-2xl font-serif leading-loose italic">
             “我们一起理解发生在你身上的事，理解这些事把你变怎么了，<br className="hidden md:block"/>理解是改变的开始。”
           </p>
           <div className="w-24 h-1 bg-rose-200 mx-auto mt-8 rounded-full opacity-60"></div>
        </div>

        {/* 第二部分：预约主卡片 */}
        <div className="max-w-4xl mx-auto bg-white shadow-[0_50px_100px_-30px_rgba(0,0,0,0.15)] border border-stone-100 rounded-sm overflow-hidden flex flex-col lg:flex-row mb-6">
          {/* 左侧：预约操作区 */}
          <div className="lg:w-[38%] bg-sage-800 p-10 md:p-12 flex flex-col items-center justify-center text-white relative">
             <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:20px_20px]"></div>
             
             <div className="text-center relative z-10 flex flex-col items-center">
                <h3 className="font-serif text-xl md:text-2xl font-black tracking-[0.2em] mb-8 text-white">预约咨询</h3>
                
                <div className="space-y-6 w-full">
                   {/* 方式一：直接点击预约 */}
                   <button 
                     onClick={() => setIsBookingOpen(true)}
                     className="w-full group relative overflow-hidden bg-white text-sage-900 px-6 py-4 rounded-sm shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-3"
                   >
                      <div className="absolute inset-0 bg-sage-100 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                      <Calendar size={20} className="relative z-10 text-sage-700" />
                      <span className="relative z-10 font-bold font-serif tracking-widest text-sm">在线填写</span>
                      <ExternalLink size={14} className="relative z-10 opacity-50" />
                   </button>

                   <div className="flex items-center gap-4 opacity-50">
                      <div className="h-[1px] bg-white flex-grow"></div>
                      <span className="text-[10px] tracking-widest uppercase">OR</span>
                      <div className="h-[1px] bg-white flex-grow"></div>
                   </div>

                   {/* 方式二：扫码 */}
                   <div className="flex flex-col items-center group">
                      <div className="bg-white p-2 rounded-sm shadow-lg mb-4 transform transition-transform group-hover:scale-105">
                        <img src={ASSETS.bookingQR} alt="预约二维码" className="w-24 h-24 object-cover" />
                      </div>
                      <p className="text-sage-200 text-xs font-serif tracking-widest opacity-80">扫码填写</p>
                   </div>
                </div>

                <p className="text-sage-100 text-[10px] mt-8 leading-relaxed font-serif max-w-[200px] opacity-70">
                   * 所有提交的信息仅供初步评估使用，严格保密。
                </p>
             </div>
          </div>

          <div className="lg:w-[62%] p-8 md:p-12 lg:pl-16 flex flex-col justify-center bg-white">
             <div className="flex items-center gap-4 mb-12">
                <div className="w-2 h-8 bg-amber-400 rounded-sm"></div>
                <h3 className="font-serif text-2xl text-stone-900 font-black tracking-wider">后续流程</h3>
             </div>
             
             <div className="space-y-10">
                {[
                  { icon: <ClipboardCheck size={22} />, title: "系统通知", desc: "提交后我将在后台实时收到申请，不用担心信息传递。" },
                  { icon: <PhoneCall size={20} />, title: "评估反馈", desc: "无论是否接受咨询，我将在24小时内主动联系你告知结果。" },
                  { icon: <ShieldCheck size={22} />, title: "协议签订", desc: "签订咨询协议开始咨询。" }
                ].map((step, i) => (
                  <div key={i} className="flex gap-6 items-start group">
                     <div className="w-12 h-12 rounded-full bg-stone-50 flex items-center justify-center shrink-0 border border-stone-100 shadow-sm transition-colors group-hover:bg-amber-50 group-hover:border-amber-100 text-stone-800">
                        {step.icon}
                     </div>
                     <div>
                        <h4 className="font-serif text-base md:text-lg text-stone-900 font-black mb-2">{step.title}</h4>
                        <p className="text-stone-800 text-sm font-serif font-medium leading-relaxed">
                          {step.desc}
                        </p>
                     </div>
                  </div>
                ))}
             </div>
          </div>
        </div>

        {/* 联系提示语 */}
        <div className="max-w-4xl mx-auto mb-32 text-center animate-fade-in flex flex-col items-center">
           <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-stone-500 font-serif text-sm">
              <div className="flex items-center gap-2">
                 <span className="text-stone-300"><Info size={14} /></span>
                 <span>如有预约疑问，可联系公众号或微信：</span>
                 <span className="text-stone-900 font-black border-b border-stone-200 select-all">xlzxcrx</span>
              </div>
              <div className="hidden md:block w-1.5 h-1.5 rounded-full bg-stone-200"></div>
              <div className="flex items-center gap-2">
                 <span>或邮件至：</span>
                 <span className="text-stone-900 font-black border-b border-stone-200 select-all">chenruixue3@qq.com</span>
              </div>
           </div>
        </div>

        {/* 商务合作区块 */}
        <div className="max-w-5xl mx-auto relative mb-12">
           <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
              <div className="absolute top-1/2 left-[35%] -translate-y-1/2 w-[400px] h-[400px] bg-amber-100/30 rounded-full blur-[100px] animate-pulse"></div>
              <div className="absolute top-1/2 left-[55%] -translate-y-1/2 w-[400px] h-[400px] bg-rose-100/30 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
              <svg className="absolute inset-0 w-full h-full opacity-[0.03]" viewBox="0 0 100 100" preserveAspectRatio="none">
                 <path d="M0,50 Q25,45 50,50 T100,50" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-stone-900" />
                 <path d="M0,55 Q25,50 50,55 T100,55" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-stone-900" />
              </svg>
           </div>

           <div className="flex flex-col md:flex-row items-center justify-between gap-12 md:gap-20 px-4">
              <div className="flex-1 text-center md:text-left">
                 <div className="inline-flex items-center gap-3 mb-6 bg-white/40 backdrop-blur-sm px-4 py-2 rounded-full border border-stone-100/50">
                    <Sparkles size={16} className="text-amber-400" />
                    <span className="text-[10px] font-mono tracking-[0.5em] text-stone-400 uppercase font-black">Co-Creation</span>
                 </div>
                 
                 <h3 className="font-serif text-4xl md:text-5xl text-stone-900 font-black mb-8 tracking-tighter relative inline-block">
                    商务合作
                    <div className="absolute -right-12 -top-4 opacity-20">
                       <Handshake size={60} strokeWidth={1} />
                    </div>
                 </h3>

                 <div className="relative">
                    <div className="absolute -left-6 top-0 bottom-0 w-[1px] bg-stone-200 hidden md:block"></div>
                    <p className="text-stone-800 font-serif text-xl md:text-2xl leading-relaxed font-medium selection:bg-rose-100">
                      诚挚欢迎心理机构、学校或企业联系，<br />
                      洽谈讲座、团体带领或专栏撰稿等合作项目。
                    </p>
                 </div>
                 
                 <div className="mt-8 flex items-center justify-center md:justify-start gap-4 text-stone-300 font-mono text-[9px] tracking-[0.6em] uppercase">
                    <span>Inquiry</span>
                    <div className="w-8 h-[1px] bg-stone-200"></div>
                    <span>Collaboration</span>
                 </div>
              </div>

              <div className="w-full md:w-[320px] shrink-0 space-y-6">
                 <div className="group/card relative bg-white border border-stone-100 p-6 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.08)] transition-all duration-700 rounded-sm">
                    <div className="flex items-center gap-4 mb-3">
                       <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center text-rose-400 group-hover/card:bg-rose-500 group-hover/card:text-white transition-all">
                          <Mail size={16} />
                       </div>
                       <span className="text-[9px] text-stone-400 font-mono tracking-widest uppercase font-black">Email Link</span>
                    </div>
                    <div className="pl-12">
                       <p className="text-stone-900 font-black text-sm md:text-base border-b border-transparent group-hover/card:border-rose-200 transition-all select-all">chenruixue3@qq.com</p>
                    </div>
                 </div>

                 <div className="group/card relative bg-white border border-stone-100 p-6 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.08)] transition-all duration-700 rounded-sm">
                    <div className="flex items-center gap-4 mb-3">
                       <div className="w-8 h-8 rounded-full bg-sage-50 flex items-center justify-center text-sage-400 group-hover/card:bg-sage-600 group-hover/card:text-white transition-all">
                          <MessageCircle size={16} />
                       </div>
                       <span className="text-[9px] text-stone-400 font-mono tracking-widest uppercase font-black">WeChat / Official</span>
                    </div>
                    <div className="pl-12">
                       <p className="text-stone-900 font-black text-sm md:text-base border-b border-transparent group-hover/card:border-sage-200 transition-all select-all">xlzxcrx</p>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </SectionWrapper>

      {/* 在线预约的全屏 Modal */}
      {isBookingOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm animate-fade-in p-4 md:p-8">
           <div className="bg-white w-full max-w-5xl h-[90vh] rounded-sm shadow-2xl relative flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-stone-100 bg-[#fcfbf9]">
                 <div className="flex items-center gap-3">
                    <Calendar className="text-sage-600" size={20} />
                    <span className="font-serif text-stone-800 font-bold tracking-wider">预约申请</span>
                 </div>
                 <button 
                   onClick={() => setIsBookingOpen(false)}
                   className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-900"
                 >
                    <X size={24} />
                 </button>
              </div>
              
              {/* Iframe Container */}
              <div className="flex-grow bg-stone-50 relative">
                 <iframe 
                   id="idy_frame"
                   src={BOOKING_URL} 
                   className="absolute inset-0 w-full h-full border-0"
                   title="预约表单"
                   allowFullScreen
                   sandbox="allow-same-origin allow-scripts allow-modals allow-downloads allow-forms allow-popups"
                   style={{ minHeight: '650px' }}
                 ></iframe>
              </div>

              {/* Footer Hint */}
              <div className="px-6 py-3 bg-white border-t border-stone-100 text-center md:text-right">
                 <p className="text-[10px] text-stone-400 font-mono uppercase tracking-widest">
                    Powered by Tencent / Secure Form
                 </p>
              </div>
           </div>
        </div>
      )}

      <style>{`
        @keyframes dash { to { stroke-dashoffset: -1000; } }
      `}</style>
    </div>
  );
};
